<template>
  <teleport to="body">
    <transition name="popup-slide">
      <div
        v-if="isVisible"
        class="fixed inset-0 z-[8500] flex items-center justify-center p-4"
        @click.self="dismiss"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]" @click="dismiss"></div>

        <!-- Popup card -->
        <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">

          <!-- Sluiten -->
          <button
            @click="dismiss"
            class="absolute top-3 right-3 z-10 text-white/60 hover:text-white transition-colors"
            aria-label="Sluiten"
          >
            <i class="bi bi-x-lg text-[1.1rem]"></i>
          </button>

          <!-- Donkere header -->
          <div class="bg-[#111] px-6 pt-8 pb-6 text-center">
            <div class="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FFD500]/15 mb-4">
              <i class="bi bi-tag-fill text-[#FFD500] text-[1.5rem]"></i>
            </div>
            <h2 class="text-white font-bold text-[1.3rem] leading-snug mb-1">
              €10 korting op je eerste bestelling
            </h2>
            <p class="text-white/60 text-[0.85rem] leading-relaxed">
              Laat je e-mail achter en ontvang direct een kortingscode.
            </p>
          </div>

          <!-- Formulier of succes -->
          <div class="px-6 py-6">

            <!-- Succes-state -->
            <div v-if="submitted" class="text-center py-2">
              <div class="w-12 h-12 rounded-full bg-[#dcfce7] flex items-center justify-center mx-auto mb-3">
                <i class="bi bi-check-lg text-[#22c55e] text-[1.3rem]"></i>
              </div>
              <p class="font-semibold text-[#111] mb-1">Je kortingscode:</p>
              <div class="bg-[#f9fafb] border-2 border-dashed border-[#FFD500] rounded-xl py-3 px-4 mb-3">
                <span class="font-mono font-bold text-[1.2rem] tracking-widest text-[#111]">WELKOM10</span>
              </div>
              <p class="text-[#888] text-[0.8rem]">Kopieer de code en gebruik hem in de checkout.</p>
              <button
                @click="dismiss"
                class="mt-4 w-full bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold py-3 rounded-xl transition-all text-[0.9rem]"
              >
                Nu winkelen <i class="bi bi-arrow-right ml-1"></i>
              </button>
            </div>

            <!-- Formulier-state -->
            <form v-else @submit.prevent="submit">
              <input
                v-model="email"
                type="email"
                required
                placeholder="jouw@email.nl"
                class="w-full border border-gray-200 rounded-xl px-4 py-3 text-[0.9rem] text-[#111] focus:outline-none focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all mb-3"
              />
              <button
                type="submit"
                class="w-full bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold py-3 rounded-xl transition-all text-[0.9rem] hover:-translate-y-[1px] hover:shadow-lg hover:shadow-[#FFD500]/30"
              >
                Geef mij €10 korting
              </button>
              <p class="text-center text-[#bbb] text-[0.75rem] mt-3">
                Geen spam. Je kunt je altijd uitschrijven.
              </p>
            </form>

          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@stores/auth'

const auth = useAuthStore()

const isVisible = ref(false)
const submitted = ref(false)
const email     = ref('')

const STORAGE_KEY = 'p2c_discount_popup_dismissed'
const DELAY_MS    = 18000  // 18 seconden inactiviteit

let timer = null

const shouldShow = () => {
  if (auth.isLoggedIn) return false
  if (typeof window === 'undefined') return false
  if (localStorage.getItem(STORAGE_KEY)) return false
  return true
}

const show = () => {
  if (!shouldShow()) return
  isVisible.value = true
}

const dismiss = () => {
  isVisible.value = false
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, '1')
  }
}

const submit = () => {
  // Toon direct de kortingscode - verstuur de aanmelding best-effort op de
  // achtergrond, zodat de UX nooit blokkeert als de mail even faalt.
  submitted.value = true

  fetch('/api/newsletter-subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.value, source: 'discount_popup' }),
  }).catch(() => {})

  // Optioneel: track GA4 event
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'discount_popup_submitted', {
      event_category: 'conversion',
      email: email.value
    })
  }
}

// Exit intent: muis verlaat viewport aan de bovenkant
const handleMouseLeave = (e) => {
  if (e.clientY <= 0 && shouldShow()) {
    clearTimeout(timer)
    show()
    document.removeEventListener('mouseleave', handleMouseLeave)
  }
}

onMounted(() => {
  if (!shouldShow()) return

  // Timer: toon na DELAY_MS als gebruiker nog op de pagina is
  timer = setTimeout(() => {
    show()
    document.removeEventListener('mouseleave', handleMouseLeave)
  }, DELAY_MS)

  // Exit intent
  document.addEventListener('mouseleave', handleMouseLeave)
})

onUnmounted(() => {
  clearTimeout(timer)
  document.removeEventListener('mouseleave', handleMouseLeave)
})
</script>

<style scoped>
.popup-slide-enter-active,
.popup-slide-leave-active {
  transition: opacity 0.25s ease;
}
.popup-slide-enter-active .relative,
.popup-slide-leave-active .relative {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease;
}
.popup-slide-enter-from,
.popup-slide-leave-to {
  opacity: 0;
}
.popup-slide-enter-from .relative {
  transform: scale(0.88) translateY(20px);
  opacity: 0;
}
</style>
